/**
 * Informe de seguimiento al plan de mejoramiento
 * (`/api/seguimientos-plan`).
 *
 * La regla de alerta C6 avisa de los planes con acciones abiertas y sin registro
 * de seguimiento en 90 días.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError, getAuthToken } from '@/services/http'

// Informe de Seguimiento al Plan de Mejoramiento / Fortalecimiento.
// GET/PUT /api/ejecuciones/{id}/seguimiento-plan?tipo=  ·  GET .../pdf  (permiso planes.ver / planes.seguimiento)

export type TipoSeguimiento = 'mejoramiento' | 'fortalecimiento'

/** Forma con la que el API de seguimiento plan devuelve este dato. */
export interface FilaSeguimiento {
  id_accion: number
  aspecto: string | null
  meta: string | null
  medicion: string | null
  por_cumplir: string | null
  fecha_inicio: string | null
  fecha_terminacion: string | null
  estrategias: string | null
  balance: number | null
}

/** Forma con la que el API de seguimiento plan devuelve este dato. */
export interface FactorSeguimiento {
  factor: string
  filas: FilaSeguimiento[]
}

/** Forma con la que el API de seguimiento plan devuelve este dato. */
export interface SeguimientoPlan {
  id_ejecucion: number
  tipo: TipoSeguimiento
  portada: {
    titulo: string
    anexo: string | null
    condicion: string | null
    programa: string | null
    codigo_snies: string | null
    resolucion: string | null
    facultad: string | null
    lugar: string | null
    fecha_elaboracion: string | null
    periodo: string
  }
  narrativa: {
    presentacion: string | null
    informe_intro: string | null
    valoracion_final: string | null
  }
  factores: FactorSeguimiento[]
  resumen: {
    promedio: number
    n_aspectos: number
    valoracion_texto: string
  }
  sin_plan: boolean
}

/** Campos editables de portada + narrativa. */
export interface SeguimientoEditable {
  anexo?: string | null
  condicion?: string | null
  codigo_snies?: string | null
  resolucion?: string | null
  lugar?: string | null
  fecha_elaboracion?: string | null
  presentacion?: string | null
  informe_intro?: string | null
  valoracion_final?: string | null
}

interface Resultado<T> {
  ok: boolean
  status: number
  data?: T
  error?: string
}

/**
 * Consulta seguimiento plan · `GET /api/ejecuciones`.
 *
 * @returns Resultado<SeguimientoPlan>
 */
export async function getSeguimientoPlanService(idEjecucion: number, tipo: TipoSeguimiento): Promise<Resultado<SeguimientoPlan>> {
  const r = await http.get<SeguimientoPlan>(`/api/ejecuciones/${idEjecucion}/seguimiento-plan?tipo=${tipo}`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo cargar el seguimiento del plan') }
}

/**
 * Guarda seguimiento plan · `PUT /api/ejecuciones`.
 *
 * @returns Resultado<unknown>
 */
export async function guardarSeguimientoPlanService(
  idEjecucion: number,
  tipo: TipoSeguimiento,
  cambios: SeguimientoEditable
): Promise<Resultado<unknown>> {
  const r = await http.put(`/api/ejecuciones/${idEjecucion}/seguimiento-plan?tipo=${tipo}`, cambios)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo guardar el seguimiento') }
}

/** Descarga binaria del PDF (fetch directo con el token de sesión). */
export async function descargarPdfSeguimientoService(idEjecucion: number, tipo: TipoSeguimiento): Promise<Resultado<null>> {
  const base = process.env.VUE_APP_API_BASE_URL ?? ''
  const token = getAuthToken()
  const res = await fetch(`${base}/api/ejecuciones/${idEjecucion}/seguimiento-plan/pdf?tipo=${tipo}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  })
  if (!res.ok) {
    return { ok: false, status: res.status, error: 'No se pudo descargar el PDF del seguimiento' }
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `seguimiento-plan-${tipo}-${idEjecucion}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
  return { ok: true, status: res.status, data: null }
}
